import { useState, useEffect, useCallback } from 'react'
import { Typography, Tabs, Spin, Alert, message } from 'antd'
import { ApiOutlined, MailOutlined, AppstoreOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { usePageTitle } from '@/contexts/PageTitleContext'
import PannelloIntegrazioneEsterna, { CampoIntegrazione } from '@/components/PannelloIntegrazioneEsterna'
import { invalidateIntegrationSettingsCache } from '@/hooks/useIntegrationSettings'

const { Title } = Typography

interface SettingItem {
  chiave: string
  valore: string | null
  descrizione: string | null
  gruppo: string
  tipo: 'string' | 'secret' | 'url' | 'boolean'
}

type SettingsMap = Record<string, SettingItem[]>

// ---------------------------------------------------------------------------
// Configurazione statica: quale pannello mostrare per ogni chiave
// ---------------------------------------------------------------------------

interface PannelloConfig {
  titolo: string
  descrizione?: string
  url?: string
  urlLabel?: string
  chiavi: string[]                          // ordine dei campi nel pannello
  extraProps?: Partial<CampoIntegrazione>[] // override per-campo (stesso ordine)
}

const PANNELLI_INTEGRAZIONI: PannelloConfig[] = [
  {
    titolo: 'Infocar Repair',
    descrizione: 'Software gestione riparazioni. L\'URL configurato viene usato per aprire la scheda veicolo/cliente direttamente dalla modale di dettaglio.',
    url: 'https://www.infocar.it',
    urlLabel: 'Infocar sito ufficiale',
    chiavi: ['infocar_repair_url', 'infocar_repair_token'],
    extraProps: [
      { placeholder: 'https://repair.infocar.it' },
      { placeholder: 'Token API (opzionale)', copiabile: false },
    ],
  },
  {
    titolo: 'openapi.it — Lookup Targa',
    descrizione: 'Fornisce dati tecnici del veicolo a partire dalla targa italiana.',
    url: 'https://openapi.it',
    urlLabel: 'openapi.it dashboard',
    chiavi: ['openapi_it_token', 'openapi_it_url'],
    extraProps: [
      { placeholder: 'Bearer xxxxxxxxxxxxxxxx', copiabile: false },
      { placeholder: 'https://api.openapi.it/automotive/IT-car' },
    ],
  },
  {
    titolo: 'WhatsApp Business',
    descrizione: 'Invio messaggi WhatsApp ai clienti tramite webhook esterno.',
    chiavi: ['whatsapp_webhook_url', 'whatsapp_api_token'],
    extraProps: [
      { placeholder: 'https://...' },
      { placeholder: 'Token API WhatsApp', copiabile: false },
    ],
  },
]

const PANNELLI_EMAIL: PannelloConfig[] = [
  {
    titolo: 'Configurazione SMTP',
    descrizione: 'Parametri per l\'invio di email transazionali (notifiche, preventivi, …).',
    chiavi: ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password'],
    extraProps: [
      { placeholder: 'smtp.gmail.com' },
      { placeholder: '587' },
      { placeholder: 'noreply@tuaoffic.ina.it' },
      { placeholder: '••••••••', copiabile: false },
    ],
  },
]

// ---------------------------------------------------------------------------

const ImpostazioniIntegrazioni = () => {
  const { setTitle } = usePageTitle()
  const [settingsMap, setSettingsMap] = useState<SettingsMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setTitle('Integrazioni')
  }, [setTitle])

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await axiosInstance.get<SettingsMap>(API_ENDPOINTS.SETTINGS)
      setSettingsMap(res.data)
    } catch {
      setError('Impossibile caricare le impostazioni')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleSave = async (chiave: string, valore: string | null) => {
    setSaving((prev) => ({ ...prev, [chiave]: true }))
    try {
      await axiosInstance.put(API_ENDPOINTS.SETTING_BY_KEY(chiave), { valore })
      invalidateIntegrationSettingsCache()
      message.success('Impostazione salvata')
      // Aggiorna in locale senza refetch
      setSettingsMap((prev) => {
        const next = { ...prev }
        for (const gruppo of Object.keys(next)) {
          next[gruppo] = next[gruppo].map((s) =>
            s.chiave === chiave ? { ...s, valore } : s
          )
        }
        return next
      })
    } catch {
      message.error('Errore durante il salvataggio')
    } finally {
      setSaving((prev) => ({ ...prev, [chiave]: false }))
    }
  }

  // Costruisce i campi per un pannello basandosi sulla configurazione e sui dati DB
  const buildCampi = (pannello: PannelloConfig): CampoIntegrazione[] => {
    const allItems = Object.values(settingsMap).flat()
    return pannello.chiavi.map((chiave, idx) => {
      const item = allItems.find((s) => s.chiave === chiave)
      const extra = pannello.extraProps?.[idx] ?? {}
      return {
        etichetta: item?.descrizione ?? chiave,
        chiave,
        tipo: item?.tipo ?? 'string',
        valore: item?.valore ?? null,
        ...extra,
      }
    })
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return <Alert type="error" message={error} style={{ margin: 24 }} />
  }

  const tabsItems = [
    {
      key: 'integrazioni',
      label: (
        <span>
          <ApiOutlined /> Integrazioni
        </span>
      ),
      children: (
        <div style={{ maxWidth: 700 }}>
          {PANNELLI_INTEGRAZIONI.map((p) => (
            <PannelloIntegrazioneEsterna
              key={p.titolo}
              titolo={p.titolo}
              descrizione={p.descrizione}
              url={p.url}
              urlLabel={p.urlLabel}
              campi={buildCampi(p)}
              saving={saving}
              onSave={handleSave}
            />
          ))}
        </div>
      ),
    },
    {
      key: 'email',
      label: (
        <span>
          <MailOutlined /> Email / SMTP
        </span>
      ),
      children: (
        <div style={{ maxWidth: 700 }}>
          {PANNELLI_EMAIL.map((p) => (
            <PannelloIntegrazioneEsterna
              key={p.titolo}
              titolo={p.titolo}
              descrizione={p.descrizione}
              url={p.url}
              urlLabel={p.urlLabel}
              campi={buildCampi(p)}
              saving={saving}
              onSave={handleSave}
            />
          ))}
        </div>
      ),
    },
    {
      key: 'altro',
      label: (
        <span>
          <AppstoreOutlined /> Altro
        </span>
      ),
      children: (
        <div style={{ maxWidth: 700 }}>
          {(settingsMap['generale'] ?? []).length > 0 ? (
            <PannelloIntegrazioneEsterna
              titolo="Impostazioni generali"
              campi={(settingsMap['generale'] ?? []).map((s) => ({
                etichetta: s.descrizione ?? s.chiave,
                chiave: s.chiave,
                tipo: s.tipo,
                valore: s.valore,
              }))}
              saving={saving}
              onSave={handleSave}
            />
          ) : (
            <Alert type="info" message="Nessuna impostazione generale configurata." />
          )}
        </div>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px 24px 0' }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        Integrazioni &amp; Configurazioni
      </Title>
      <Tabs defaultActiveKey="integrazioni" items={tabsItems} />
    </div>
  )
}

export default ImpostazioniIntegrazioni
